import React, { useRef, useState } from "react";

import { themevals } from "theme/themevals";
import { messageToHtml, buildProposalEmailHtml } from "helpers/messageFormat";

const escHtml = (s: string) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** What the rest of the email will contain, for the full-email Preview. */
export interface EmailPreviewContext {
  /** The proposal link; null before it exists (a placeholder is shown). */
  shareUrl?: string | null;
  /** The link's password; null when unknown yet (an example is shown) or none. */
  password?: string | null;
  /** Is there a password on the link at all? */
  hasPassword: boolean;
  /** Is it included in this email (vs a separate one)? */
  includePassword: boolean;
  fromName: string;
  replyTo?: string | null;
  subject?: string | null;
  to?: string | null;
}

/**
 * The email message box for proposal sends (2026-09-28). A roomy textarea
 * with a small toolbar - Bold, Italic, Bulleted list, Numbered list, Link -
 * that inserts the few markers the email understands, and a Preview that
 * renders exactly what the recipient gets. Deliberately not a rich-text
 * editor: the text is plain with light markers, so it is stored, logged
 * and emailed the same way and nothing pasted can break the email.
 */
export default function MessageEditor({
  value,
  onChange,
  placeholder,
  minHeight = 180,
  email,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  minHeight?: number;
  /**
   * Proposal emails: adds Insert Button / Link / Password (placement tokens)
   * and makes Preview show the WHOLE email as it will arrive (2026-09-28).
   */
  email?: EmailPreviewContext;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [preview, setPreview] = useState(false);

  /** Wrap the selection (or a placeholder word) in `before`/`after`. */
  const wrap = (before: string, after: string, fallback: string) => {
    const el = ref.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const picked = value.slice(s, e) || fallback;
    onChange(value.slice(0, s) + before + picked + after + value.slice(e));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + before.length, s + before.length + picked.length);
    });
  };

  /** Turn the selected lines (or the current line) into a list. */
  const list = (ordered: boolean) => {
    const el = ref.current;
    if (!el) return;
    const s = value.lastIndexOf("\n", el.selectionStart - 1) + 1;
    const eIdx = value.indexOf("\n", el.selectionEnd);
    const e = eIdx === -1 ? value.length : eIdx;
    const block = value
      .slice(s, e)
      .split("\n")
      .map((l, i) => {
        const bare = l.replace(/^\s*(?:[-*\u2022]|\d+[.)])\s+/, "");
        return ordered ? `${i + 1}. ${bare}` : `- ${bare}`;
      })
      .join("\n");
    onChange(value.slice(0, s) + block + value.slice(e));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + block.length, s + block.length);
    });
  };

  const link = () => {
    const url = window.prompt("Link address (https://...)", "https://");
    if (!url || !/^(https?:\/\/|mailto:)\S+$/i.test(url.trim())) return;
    wrap("[", `](${url.trim()})`, "link text");
  };

  /** Insert a placement token at the cursor; {button} goes on its own line. */
  const token = (t: "button" | "link" | "password") => {
    const el = ref.current;
    const s = el ? el.selectionStart : value.length;
    const e = el ? el.selectionEnd : value.length;
    let ins = `{${t}}`;
    if (t === "button") {
      const before = value.slice(0, s);
      const after = value.slice(e);
      ins = `${before && !before.endsWith("\n\n") ? (before.endsWith("\n") ? "\n" : "\n\n") : ""}{button}${after && !after.startsWith("\n") ? "\n\n" : ""}`;
    }
    onChange(value.slice(0, s) + ins + value.slice(e));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(s + ins.length, s + ins.length);
    });
  };

  const previewHtml = () => {
    if (!email) return value.trim() ? messageToHtml(value) : '<p style="color:#999;font-size:13px">Nothing written yet.</p>';
    const password = email.hasPassword ? email.password || "ABC-2027 (set when sent)" : null;
    const shareUrl = email.shareUrl || "https://getmulch.com/p/\u2026 (created when sent)";
    const head =
      `<div style="font-size:12px;color:#666;border-bottom:1px solid #eee;padding-bottom:8px;margin-bottom:14px">` +
      `${email.to ? `<div><b>To:</b> ${escHtml(email.to)}</div>` : ""}` +
      `${email.subject ? `<div><b>Subject:</b> ${escHtml(email.subject)}</div>` : ""}` +
      `</div>`;
    return (
      (email.to || email.subject ? head : "") +
      buildProposalEmailHtml({ message: value, shareUrl, password, includePassword: email.includePassword, fromName: email.fromName, replyTo: email.replyTo ?? null })
    );
  };

  const btn: React.CSSProperties = {
    background: "#fff",
    border: `1px solid ${themevals.colors.inputOutline}`,
    borderRadius: 5,
    padding: "3px 9px",
    fontFamily: "Poppins",
    fontSize: 12,
    cursor: "pointer",
    color: "#1a2332",
    lineHeight: 1.4,
  };
  const tab = (on: boolean): React.CSSProperties => ({
    ...btn,
    border: "none",
    background: "none",
    fontWeight: on ? 600 : 400,
    color: on ? "#1a2332" : "#818181",
    borderBottom: on ? "2px solid #1a2332" : "2px solid transparent",
    borderRadius: 0,
  });

  return (
    <div style={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: 6, background: "#fff", textAlign: "left" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", borderBottom: `1px solid ${themevals.colors.inputOutline}`, background: "#FAFAF8", flexWrap: "wrap" }}>
        <button type="button" style={tab(!preview)} onClick={() => setPreview(false)}>Write</button>
        <button type="button" style={tab(preview)} onClick={() => setPreview(true)}>Preview</button>
        {!preview && (
          <>
            <span style={{ width: 1, height: 18, background: themevals.colors.inputOutline, margin: "0 4px" }} />
            <button type="button" style={{ ...btn, fontWeight: 700 }} title="Bold" onClick={() => wrap("**", "**", "bold text")}>B</button>
            <button type="button" style={{ ...btn, fontStyle: "italic" }} title="Italic" onClick={() => wrap("*", "*", "italic text")}>I</button>
            <button type="button" style={btn} title="Bulleted list" onClick={() => list(false)}>{"\u2022 List"}</button>
            <button type="button" style={btn} title="Numbered list" onClick={() => list(true)}>1. List</button>
            <button type="button" style={btn} title="Link" onClick={link}>Link</button>
            {email && (
              <>
                <span style={{ width: 1, height: 18, background: themevals.colors.inputOutline, margin: "0 4px" }} />
                <span style={{ fontSize: 11, color: "#818181", fontFamily: "Poppins" }}>Place:</span>
                <button type="button" style={btn} title="Put the View your proposal button here" onClick={() => token("button")}>Button</button>
                <button type="button" style={btn} title="Put the plain proposal link here" onClick={() => token("link")}>Proposal link</button>
                {email.hasPassword && (
                  <button type="button" style={btn} title="Put the password here" onClick={() => token("password")}>Password</button>
                )}
              </>
            )}
          </>
        )}
      </div>
      {email && !preview && (
        <div style={{ padding: "6px 12px", fontSize: 11, color: "#818181", fontFamily: "Poppins", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
          {"Without placement the button sits above your message and the link below it. Use Place to put them where you want; Preview shows the whole email."}
        </div>
      )}
      {preview ? (
        <div
          style={{ minHeight, padding: "12px 14px", fontFamily: "Arial, Helvetica, sans-serif", color: "#222" }}
          // Built by the same code as the email; user text is escaped before tags are added.
          dangerouslySetInnerHTML={{ __html: previewHtml() }}
        />
      ) : (
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{ display: "block", width: "100%", boxSizing: "border-box", minHeight, border: "none", outline: "none", resize: "vertical", padding: "10px 12px", fontFamily: "Poppins", fontSize: 13, lineHeight: 1.6, borderRadius: "0 0 6px 6px" }}
        />
      )}
    </div>
  );
}
